import { Article } from '../../types/strapi';

import { ContentItem, ContentMedia, ContentPerson } from './contentApi';

/**
 * Content API items into the CMS-shaped rows the hub components already render.
 *
 * The transport changed, the row shape did not: every page keeps reading `url`,
 * `published_date`, `post_category` and the rest, so this file is the only place that knows
 * the payload now arrives with its card fields under `meta`.
 */

export interface ResearchUpdateRow {
  id?: number | string;
  date: string;
  preview?: string;
  image?: { url?: string };
  author?: { name?: string; title?: string; icon?: { url?: string } };
  areas_covered?: { label: string }[];
  summary?: string;
  locked?: boolean;
}

export interface MarketUpdateRow {
  id?: number | string;
  title: string;
  url?: string;
  published_date?: string;
  takeaway?: string;
  cover?: { url?: string };
  author?: { name?: string; title?: string };
  source_links?: { header: string; url: string }[];
  content_detail_alternate_url?: string;
  is_corsia?: boolean;
  content?: string;
  body?: string;
  locked?: boolean;
}

const media = (value: ContentMedia | null | undefined) =>
  value?.url
    ? {
        url: value.url,
        name: value.alternativeText ?? '',
        alternativeText: value.alternativeText ?? '',
      }
    : undefined;

const person = (value: ContentPerson | null | undefined) =>
  value ? { name: value.name, title: value.title } : undefined;

export const toArticle = (item: ContentItem): Article => {
  const meta = item.meta ?? {};
  return {
    id: meta.strapi_id ?? 0,
    documentId: item.external_id,
    title: item.title,
    published_date: item.published_date ?? '',
    // null on every listing (the feed carries no bodies) and on a locked row. `teaser` is
    // what a card has instead, so nothing renders an empty body as if it were the article.
    content: item.body ?? '',
    teaser: item.teaser ?? '',
    url: item.slug,
    visibility: item.visibility ?? undefined,
    locked: item.locked,
    lock_reason: item.lock_reason,
    cover: media(meta.cover),
    author: person(meta.author),
    post_category: meta.post_category ?? undefined,
    featured_summary: meta.featured_summary ?? '',
    is_featured: meta.is_featured ?? null,
    display_priority: meta.display_priority ?? null,
    isPinnedRelatedContent: meta.is_pinned_related_content === true,
    content_detail_alternate_url: meta.content_detail_alternate_url ?? undefined,
    hubspot_form_id: meta.hubspot_form_id ?? undefined,
    resource_post_file_for_platform: media(meta.resource_file),
    // Names, because the Compass check on the detail page reads `project_types[].name`.
    project_types: (item.project_types ?? []).map((name) => ({ name })),
    // Slugs, not rows: the curated list is resolved against a listing the caller may see,
    // so a link to content they cannot open never becomes a card that goes nowhere.
    next_up_slugs: meta.next_up_slugs ?? [],
    updatedAt: meta.updated_at ?? '',
  };
};

export const toResearchUpdate = (item: ContentItem): ResearchUpdateRow => {
  const meta = item.meta ?? {};
  return {
    id: meta.strapi_id ?? undefined,
    date: item.slug,
    preview: meta.preview ?? '',
    image: media(meta.image),
    author: person(meta.author),
    areas_covered: (meta.areas_covered ?? []).filter(Boolean).map((label) => ({ label })),
    summary: item.body ?? '',
    locked: item.locked,
  };
};

export const toMarketUpdate = (item: ContentItem): MarketUpdateRow => {
  const meta = item.meta ?? {};
  return {
    id: meta.strapi_id ?? undefined,
    title: item.title,
    url: item.slug,
    published_date: item.published_date ?? undefined,
    takeaway: meta.takeaway ?? undefined,
    cover: media(meta.cover),
    author: person(meta.author),
    source_links: meta.source_links ?? [],
    content_detail_alternate_url: meta.content_detail_alternate_url ?? undefined,
    is_corsia: meta.is_corsia === true,
    content: item.body ?? undefined,
    locked: item.locked,
  };
};
